import React from 'react'
import styled from 'styled-components'
import { Editor as SlateEditor } from 'slate'

import { cloneElementArray } from '../../utils'
import { ClassPlugin } from './plugins/Class'
import { BoldButton, BoldPlugin } from './plugins/Bold'
import { ItalicButton, ItalicPlugin } from './plugins/Italic'
import { UnderlineButton, UnderlinePlugin } from './plugins/Underline'
import {
  AlignLeftButton,
  AlignCenterButton,
  AlignRightButton,
  AlignJustifyButton,
  AlignmentPlugin,
} from './plugins/Alignment'
import { TablePlugin } from './plugins/Table'
import TableButton from './plugins/Table/TableButtons'
import { UlButton, OlButton, ListPlugin } from './plugins/List'
import RichContentPastePlugin from './plugins/RichContentPaste'
import {
  ParagraphStyleSelectorPlugin,
  ParagraphStyleSelector,
} from './plugins/ParagraphStyleSelector'
import { NullRenderPlugin } from './plugins/NullRender'
import { ImagePlugin, ImageButton } from './plugins/Image'
import InsertBlockOnEnter from 'slate-insert-block-on-enter'
import { ColorSelectorPlugin } from './plugins/TextColorSelector'

export const defaultStyle = `
  h1, h2, h3, h4, h5, h6, h7, p, span, div {
    font-family: Tahoma, Verdana, Segoe, sans-serif;
    line-height: 1em;
  }
  h1, h2, h3, h4, h5, h6, h7, .size-6, .size-7 {
    color: #1069ab;
    font-weight: bold;
  }
  h1 {
    font-size: 18pt;
  }
  h2 {
    color: #00000a;
    font-size: 18pt;
  }
  .size-6 {
    font-size: 28pt;
    text-align: center;
  }
  .size-7 {
    font-size: 32pt;
    color: #00000a;
    text-align: center;
  }
  p, span {
    color: #00000a;
    font-size: 9pt;
    margin: 0;
  }
  ul, ol {
    margin: 0;
    padding-left: 2.6em;
  }
  ul {
    list-style-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAsAAAALCAYAAACprHcmAAAACXBIWXMAAA9nAAAPZwEoPOPyAAAApElEQVR4nGP5//8/AzrYeeX5f3cdSUZ0cRZ0gaAph//vv/aSYUKUyf94GwVGnIphCkGgYNkZEIWiAa44cNKR/wduvESxBV0DC0LhCwy3o2tgqVt/GadCZA2mSkL/WZoCdRlP3Xv7/+TdNzgVtwbrM2hJ8TGCnbGj2IHRo/cAVg0tQIVZzqoIN+PSAFKYDVWIohhdA7pCDMUwDWvOPP4fYiKLEYMALz9ZbICECmcAAAAASUVORK5CYII=);
  }
  ol {
    list-style-image: none;
  }
  td {
    vertical-align: top;
  }
`

const Container = styled.div`
  border: 2px solid #eeeeee;
  ${({ selectedStyle = '' }) => selectedStyle};
`
const Toolbar = styled.div`
  height: 36px;
  border-bottom: 2px solid #eeeeee;
`
const Content = styled.div`
  padding: 1em;
  table {
    border-collapse: collapse;
  }
  td {
    border: 1px solid black;
  }
`

const EditorToolbar = (
  { children, plugins, ...restProps }, // eslint-disable-line no-unused-vars
) => <Toolbar>{cloneElementArray(children, restProps)}</Toolbar>

const EditorContainer = ({ children, selectedStyle, ...restProps }) => (
  <Container selectedStyle={selectedStyle}>
    {cloneElementArray(children, restProps)}
  </Container>
)

const EditorContent = ({ children, ...restProps }) => (
  <Content>{cloneElementArray(children, restProps)}</Content>
)
const ButtonSpacer = styled.span`
  &::before {
    content: '|';
    font-weight: bold;
  }
  padding: 2px;
`

export const getPlugins = ({ onChange } = {}) => [
  InsertBlockOnEnter({
    kind: 'block',
    type: 'paragraph',
    nodes: [{ kind: 'text', text: '', ranges: [] }],
  }),
  ClassPlugin(),
  BoldPlugin(),
  ItalicPlugin(),
  UnderlinePlugin(),
  AlignmentPlugin(),
  TablePlugin(),
  ListPlugin(),
  RichContentPastePlugin(),
  ParagraphStyleSelectorPlugin(),
  ColorSelectorPlugin(),
  NullRenderPlugin(),
  ImagePlugin({ onChange }),
  //   FontFamilyPlugin(),
  //   FontSizePlugin(fontSizePluginOptions),
  //   ColorPlugin(),
  //   LinkPlugin(),
  //   ImagePlugin(),
  //   EmbedPlugin(),
]

class Editor extends React.Component {
  onChange = editorState => {
    this.props.onChange(editorState)
  }
  render() {
    const {
      state,
      selectedStyle = defaultStyle,
      removeButtons = [],
      unitImages,
      ...restProps
    } = this.props
    return (
      <EditorContainer
        spellCheck
        selectedStyle={selectedStyle}
        plugins={getPlugins({ onChange: this.onChange })}
        placeholder={'Enter some content...'}
        // schema={schema}
        state={state}
        editorState={state}
        onChange={this.onChange}
        {...restProps}>
        <EditorToolbar>
          {!removeButtons.includes('styleSelector') && (
            <ParagraphStyleSelector />
          )}
          {!removeButtons.includes('styleSelector') &&
          !removeButtons.includes('fontStyle') && <ButtonSpacer />}
          {!removeButtons.includes('fontStyle') && <BoldButton />}
          {!removeButtons.includes('fontStyle') && <ItalicButton />}
          {!removeButtons.includes('fontStyle') && <UnderlineButton />}
          {!removeButtons.includes('alignment') && <ButtonSpacer />}
          {!removeButtons.includes('alignment') && <AlignLeftButton />}
          {!removeButtons.includes('alignment') && <AlignCenterButton />}
          {!removeButtons.includes('alignment') && <AlignRightButton />}
          {!removeButtons.includes('alignment') && <AlignJustifyButton />}
          {!removeButtons.includes('table') && <ButtonSpacer />}
          {!removeButtons.includes('table') && <TableButton />}
          {!removeButtons.includes('list') && <ButtonSpacer />}
          {!removeButtons.includes('list') && <UlButton />}
          {!removeButtons.includes('list') && <OlButton />}
          {!removeButtons.includes('image') && <ButtonSpacer />}
          {!removeButtons.includes('image') && (
            <ImageButton unitImages={unitImages} />
          )}
        </EditorToolbar>
        <EditorContent>
          <SlateEditor state={state} />
        </EditorContent>
      </EditorContainer>
    )
  }
}

export default Editor



// WEBPACK FOOTER //
// ./src/components/Editor/index.js